import React, { useEffect, useState } from 'react';
import { fetchProspectingQueue, fetchOppsQueue } from '../api/monday';
import { BROKERS } from '../config/brokers';

const C = {
  indigo:        '#534AB7',
  indigoDark:    '#3C3489',
  indigoLight:   '#EEEDFE',
  teal:          '#1D9E75',
  tealLight:     '#E1F5EE',
  tealDark:      '#085041',
  amber:         '#633806',
  amberLight:    '#FAEEDA',
  red:           '#791F1F',
  redLight:      '#FCEBEB',
  bgPage:        '#f5f4f1',
  bgCard:        '#ffffff',
  bgSoft:        '#f0efe9',
  border:        '#e8e6e0',
  textPrimary:   '#111827',
  textSecondary: '#6b7280',
  textTertiary:  '#9ca3af',
};

function getFirstName(fullName) {
  if (!fullName) return 'there';
  return fullName.split(/\s+/)[0];
}

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

// ── StatTile ──────────────────────────────────────────────────────────────────
function StatTile({ label, value, bg, color, loading }) {
  return (
    <div style={{
      flex: 1, borderRadius: '12px', padding: '14px 12px',
      backgroundColor: bg, textAlign: 'center',
    }}>
      <div style={{ fontSize: '26px', fontWeight: '700', color, lineHeight: 1, marginBottom: '4px' }}>
        {loading ? '—' : value}
      </div>
      <div style={{ fontSize: '10px', fontWeight: '700', color, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
        {label}
      </div>
    </div>
  );
}

// ── NavCard ───────────────────────────────────────────────────────────────────
function NavCard({ emoji, title, description, bg, color, onClick }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: '100%', textAlign: 'left',
        backgroundColor: C.bgCard, border: `1.5px solid ${C.border}`,
        borderRadius: '14px', padding: '16px 18px',
        marginBottom: '10px', cursor: 'pointer',
        display: 'flex', alignItems: 'center', gap: '14px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        fontFamily: 'inherit',
      }}
    >
      <div style={{
        width: '44px', height: '44px', borderRadius: '12px',
        backgroundColor: bg, display: 'flex', alignItems: 'center',
        justifyContent: 'center', fontSize: '22px', flexShrink: 0,
      }}>
        {emoji}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: '15px', fontWeight: '700', color: C.textPrimary, marginBottom: '2px' }}>
          {title}
        </div>
        <div style={{ fontSize: '12px', color: C.textSecondary }}>
          {description}
        </div>
      </div>
      <div style={{ fontSize: '18px', color: C.textTertiary }}>›</div>
    </button>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
export default function Home({ brokerName, isAdmin, viewAs, onViewAs, onGoToProspect, onGoToOpps }) {
  const [queueData, setQueueData]   = useState(null);
  const [oppsData, setOppsData]     = useState(null);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetchProspectingQueue(brokerName),
      fetchOppsQueue(brokerName),
    ]).then(([q, o]) => {
      setQueueData(q);
      setOppsData(o);
      setLoading(false);
    }).catch(() => {
      setLoading(false);
    });
  }, [brokerName]);

  const overdue   = queueData ? queueData.overdue.length   : 0;
  const dueToday  = queueData ? queueData.dueToday.length  : 0;
  const totalOpps = oppsData  ? (oppsData.newLeads.length + oppsData.inSequence.length + oppsData.connected.length + oppsData.closed.length) : 0;
  const connected = oppsData  ? oppsData.connected.length : 0;

  return (
    <div style={{
      fontFamily: "'Inter', 'Segoe UI', system-ui, Arial, sans-serif",
      maxWidth: '720px', margin: '0 auto',
      padding: '0 0 24px', backgroundColor: C.bgPage, minHeight: '100vh',
    }}>
      {/* Header */}
      <div style={{
        padding: '28px 18px 20px',
        borderBottom: `1px solid ${C.border}`,
        backgroundColor: C.bgCard,
        marginBottom: '16px',
      }}>
        <div style={{ fontSize: '11px', fontWeight: '600', color: C.textTertiary, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '6px' }}>
          SterlingCRE
        </div>
        <h1 style={{ fontSize: '22px', fontWeight: '700', color: C.textPrimary, margin: '0 0 4px' }}>
          {getGreeting()}, {getFirstName(brokerName)} 👋
        </h1>
        <p style={{ fontSize: '13px', color: C.textSecondary, margin: 0 }}>
          Here's a snapshot of your pipeline.
        </p>
      </div>

      {/* Stats grid */}
      <div style={{ padding: '0 12px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
          <StatTile
            label="Overdue"
            value={overdue}
            bg={overdue > 0 ? C.redLight : C.bgSoft}
            color={overdue > 0 ? C.red : C.textSecondary}
            loading={loading}
          />
          <StatTile
            label="Due Today"
            value={dueToday}
            bg={dueToday > 0 ? C.amberLight : C.bgSoft}
            color={dueToday > 0 ? C.amber : C.textSecondary}
            loading={loading}
          />
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <StatTile
            label="Total Opps"
            value={totalOpps}
            bg={C.indigoLight}
            color={C.indigoDark}
            loading={loading}
          />
          <StatTile
            label="Connected"
            value={connected}
            bg={C.tealLight}
            color={C.tealDark}
            loading={loading}
          />
        </div>
      </div>

      {/* Nav cards */}
      <div style={{ padding: '0 12px' }}>
        <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', color: C.textTertiary, marginBottom: '10px' }}>
          Go to
        </div>
        <NavCard
          emoji="📋"
          title="Prospect Queue"
          description={loading ? 'Loading…' : `${overdue > 0 ? `${overdue} overdue · ` : ''}${dueToday} due today`}
          bg={C.indigoLight}
          color={C.indigo}
          onClick={onGoToProspect}
        />
        <NavCard
          emoji="🏢"
          title="New Opportunities"
          description={loading ? 'Loading…' : `${totalOpps} total · ${connected} connected`}
          bg={C.tealLight}
          color={C.teal}
          onClick={onGoToOpps}
        />
      </div>

      {/* Admin: View as another broker */}
      {isAdmin && (
        <div style={{ padding: '16px 12px 0' }}>
          <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '0.08em', textTransform: 'uppercase', color: C.textTertiary, marginBottom: '10px' }}>
            View as
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {BROKERS.filter(b => !b.isAdmin).map(b => {
              const active = viewAs === b.name;
              return (
                <button
                  key={b.name}
                  onClick={() => onViewAs(active ? null : b.name)}
                  style={{
                    padding: '7px 14px', borderRadius: '99px', border: 'none',
                    fontSize: '13px', fontWeight: active ? '700' : '500',
                    backgroundColor: active ? C.indigo : C.bgSoft,
                    color: active ? '#fff' : C.textSecondary,
                    cursor: 'pointer', fontFamily: 'inherit',
                  }}
                >
                  {b.name.split(' ')[0]}
                </button>
              );
            })}
          </div>
          {viewAs && (
            <p style={{ fontSize: '12px', color: C.textTertiary, margin: '8px 0 0' }}>
              Viewing as <strong>{viewAs}</strong> — tap their name again to return to your view
            </p>
          )}
        </div>
      )}
    </div>
  );
}
